import React, { useState, useEffect } from 'react';
import {
    useCartContext,
    useModalsContext,
    useStoreConfigContext,
} from '/src/providers';
import { Modal, H2, Loader, Section } from '/src/components';
import * as styles from './styles.scss';

const ItemsList = ({
    items,
    shoppingCartDisplayPrice,
    formatPrice,
    translate,
    title,
}) => {
    const [isActive, setIsActive] = useState(false);

    const getPackageSize = (additionalAttributeData, displayWeight) => {
        const { value } = additionalAttributeData?.find(({ option_id }) => option_id === displayWeight) || {};

        return value;
    };

    return (
        !!items?.length &&
        <div className={styles.itemsWrapper}>
            <div
                className={`${styles.sub} ${isActive ? styles.up : styles.down}`}
                onClick={() => setIsActive(!isActive)}
            >
                {title}
            </div>
            <div className={`${styles.wrap} ${isActive ? styles.active : styles.none}`}>
                {
                    items.map(({
                        product: {
                            sku,
                            display_weight,
                            producttypeinenglish,
                            additional_attribute_data,
                            image: {
                                url,
                                label,
                            } = {},
                        },
                        prices: {
                            row_total,
                            row_total_including_tax
                        },
                        quantity,
                    }) => (
                        <div
                            key={sku}
                            className={styles.item}
                        >
                            <img src={`${url}?d=32x32`} alt={label} width="32px" height="32px"/>
                            <div className={styles.name}>
                                {producttypeinenglish}
                            </div>
                            <div className={styles.det}>
                                <div className={styles.price}>
                                    {formatPrice(!!shoppingCartDisplayPrice ? row_total_including_tax : row_total)}
                                </div>
                                <div className={styles.qty}>
                                    {`${getPackageSize(additional_attribute_data, display_weight)}, ${quantity}x${translate('QTY')}`}
                                </div>
                            </div>
                        </div>
                    ))
                }
            </div>
        </div>
    );
};

const MergeCarts = ({ guestCart, }) => {
    const {
        storeConfig: {
            shopping_cart_display_price,
        },
        formatPrice,
        translate } = useStoreConfigContext();
    const { showModal, hideModal, MODALS_TYPES } = useModalsContext();
    const {
        cart: {
            id,
            itemsV2: {
                items: customerItems,
            } = {}
        },
        isLoadingCart,
        mergeCarts,
    } = useCartContext();
    const [duplicate, setDuplicate] = useState([]);

    const {
        itemsV2: {
            items: guestItems,
        } = {}
    } = guestCart;

    const handleMergeCarts = action => {
        mergeCarts({
            source_cart_id: guestCart.id,
            destination_cart_id: id,
            action,
        }).then(() => {
            hideModal();
        }).catch(errors => showModal(MODALS_TYPES.ERROR, { errors }));
    };

    const handleClose = () => handleMergeCarts('KEEP_GUEST');

    useEffect(() => {
        const same = [];

        guestItems?.forEach(({ product: { sku: guestProductSku } }) => {
            if (customerItems?.find(({ product: { sku }}) => sku === guestProductSku)) {
                same.push(guestProductSku);
            }
        });

        setDuplicate([ ...same ]);
    }, []);

    return (
        <Modal
            { ...(!isLoadingCart ? { toggle: handleClose } : {}) }
            className={styles.merge}
        >
            <Loader isLoading={isLoadingCart}>
                <H2>{translate('Do you want to combine your baskets?')}</H2>
                <div className={styles.details}>
                    {translate('It looks like you have multiple baskets and we want to make sure you have all the products you want')}
                </div>
                <div className={styles.block}>
                    <Section className={styles.section}>
                        <div className={styles.title}>
                            {translate('Current basket')}
                        </div>
                        <div>
                            <ItemsList
                                items={
                                    guestItems.filter(({ product: { sku }}) => duplicate.includes(sku))
                                }
                                shoppingCartDisplayPrice={shopping_cart_display_price}
                                formatPrice={formatPrice}
                                translate={translate}
                                title={translate('Duplicate products')}
                            />
                        </div>
                        <div className={styles.dif}>
                            <ItemsList
                                items={
                                    guestItems.filter(({ product: { sku }}) => !duplicate.includes(sku))
                                }
                                shoppingCartDisplayPrice={shopping_cart_display_price}
                                formatPrice={formatPrice}
                                translate={translate}
                                title={translate('Different products')}
                            />
                        </div>
                        <button
                            className={styles.btn}
                            onClick={() => handleMergeCarts('KEEP_GUEST')}
                            disabled={isLoadingCart}
                        >
                            {translate('Only keep current basket')}
                        </button>
                    </Section>
                    <Section className={styles.section}>
                        <div className={styles.title}>
                            {translate('Saved basket')}
                        </div>
                        <div>
                            <ItemsList
                                items={
                                    customerItems.filter(({ product: { sku }}) => duplicate.includes(sku))
                                }
                                shoppingCartDisplayPrice={shopping_cart_display_price}
                                formatPrice={formatPrice}
                                translate={translate}
                                title={translate('Duplicate products')}
                            />
                        </div>
                        <div className={styles.dif}>
                            <ItemsList
                                items={
                                    customerItems.filter(({ product: { sku }}) => !duplicate.includes(sku))
                                }
                                shoppingCartDisplayPrice={shopping_cart_display_price}
                                formatPrice={formatPrice}
                                translate={translate}
                                title={translate('Different products')}
                            />
                        </div>
                        <button
                            className={styles.btn}
                            onClick={() => handleMergeCarts('KEEP_CUSTOMER')}
                            disabled={isLoadingCart}
                        >
                            {translate('Only keep saved basket')}
                        </button>
                    </Section>
                </div>
                <div className={styles.bottom}>
                    <button
                        className={`${styles.btn} ${styles.combine}`}
                        onClick={() => handleMergeCarts('COMBINE')}
                        disabled={isLoadingCart}
                    >
                        {translate('Combine baskets')}
                    </button>
                    <button
                        className={`${styles.btn} ${styles.noDuplicates}`}
                        onClick={() => handleMergeCarts('COMBINE_NO_DUPLICATES')}
                        disabled={isLoadingCart}
                    >
                        {translate('Combine (without duplicates)')}
                    </button>
                </div>
            </Loader>
        </Modal>
    );
};

export default MergeCarts;
